import api from './client';

export type KnowledgeSourceType =
  | 'book'
  | 'article'
  | 'manual'
  | 'research'
  | 'notes'
  | 'text'
  | 'url';

export type KnowledgeSourceStatus = 'processing' | 'ready' | 'failed';

export interface KnowledgeSource {
  id: string;
  title: string;
  author: string | null;
  category: string | null;
  sourceType: KnowledgeSourceType;
  status: KnowledgeSourceStatus;
  description: string | null;
  tags: string[] | null;
  errorMessage: string | null;
  chunkCount: number;
  characterCount: number;
  fileName: string | null;
  mimeType: string | null;
  fileSize: number | null;
  sourceUrl: string | null;
  embeddingModel: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeStats {
  totalSources: number;
  readySources: number;
  processingSources: number;
  failedSources: number;
  totalChunks: number;
  totalCharacters: number;
  embeddingsEnabled: boolean;
  categories: Array<{ category: string; count: number }>;
}

export interface KnowledgeSearchResult {
  chunkId: string;
  sourceId: string;
  sourceTitle: string;
  author: string | null;
  category: string | null;
  chunkIndex: number;
  content: string;
  score: number;
}

export interface CreateTextSourcePayload {
  title: string;
  content: string;
  author?: string;
  category?: string;
  sourceType?: KnowledgeSourceType;
  description?: string;
  tags?: string[];
  sourceUrl?: string;
}

export interface UpdateSourcePayload {
  title?: string;
  author?: string;
  category?: string;
  sourceType?: KnowledgeSourceType;
  description?: string;
  tags?: string[];
  sourceUrl?: string;
}

export interface UploadSourceMeta {
  title?: string;
  author?: string;
  category?: string;
  description?: string;
  tags?: string[];
}

export const knowledgeApi = {
  async list(filters?: {
    category?: string;
    status?: KnowledgeSourceStatus;
    search?: string;
    bustCache?: boolean;
  }): Promise<KnowledgeSource[]> {
    const params: Record<string, string> = {};
    if (filters?.category) params.category = filters.category;
    if (filters?.status) params.status = filters.status;
    if (filters?.search) params.search = filters.search;
    if (filters?.bustCache) params._ts = Date.now().toString();
    const response = await api.get<KnowledgeSource[]>('knowledge', params);
    return response.data || [];
  },

  async get(id: string): Promise<KnowledgeSource> {
    const response = await api.get<KnowledgeSource>(`knowledge/${id}`);
    return response.data;
  },

  async stats(): Promise<KnowledgeStats> {
    const response = await api.get<KnowledgeStats>('knowledge/stats');
    return response.data;
  },

  async createText(payload: CreateTextSourcePayload): Promise<KnowledgeSource> {
    const response = await api.post<KnowledgeSource>('knowledge', payload);
    return response.data;
  },

  async upload(file: File, meta: UploadSourceMeta): Promise<KnowledgeSource> {
    const form = new FormData();
    form.append('file', file);
    if (meta.title) form.append('title', meta.title);
    if (meta.author) form.append('author', meta.author);
    if (meta.category) form.append('category', meta.category);
    if (meta.description) form.append('description', meta.description);
    if (meta.tags && meta.tags.length > 0) form.append('tags', meta.tags.join(','));
    const response = await api.postWithTimeout<KnowledgeSource>(
      'knowledge/upload',
      form,
      120000,
    );
    return response.data;
  },

  async update(id: string, payload: UpdateSourcePayload): Promise<KnowledgeSource> {
    const response = await api.patch<KnowledgeSource>(`knowledge/${id}`, payload);
    return response.data;
  },

  async reindex(id: string): Promise<KnowledgeSource> {
    const response = await api.post<KnowledgeSource>(`knowledge/${id}/reindex`);
    return response.data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`knowledge/${id}`);
  },

  async search(query: string, limit = 6): Promise<KnowledgeSearchResult[]> {
    const response = await api.get<{ results: KnowledgeSearchResult[] }>(
      'knowledge/search',
      { q: query, limit: limit.toString() },
    );
    return response.data?.results || [];
  },
};

export default knowledgeApi;
